import React, { useState, useEffect } from 'react';
import { useParams, Link } from "react-router-dom";
import NavbarBottom from './NavbarBottom';
import no_image_available from '../images/no_image_available.jpg';
import {
    Calendar,
    Clock,
    Share2,
    Link2,
    ArrowLeft,
    Newspaper,
    Eye,
    ChevronRight,
    User,
    Tag
} from 'lucide-react';

function DetailBlog() {
    const params = useParams();
    const [newsDetail, setNewsDetail] = useState(null);
    const [newsList, setNews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [relatedNews, setRelatedNews] = useState([]);
    const urlImg = `${process.env.REACT_APP_BASEURL}/berita/`;

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Fetch detail berita
                const detailResponse = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/news-detail/${params.id}`);
                const detailData = await detailResponse.json();
                setNewsDetail(detailData.data[0]);

                // Fetch semua berita
                const newsResponse = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/news`);
                const newsData = await newsResponse.json();
                const allNews = newsData.data.data || [];
                setNews(allNews);

                // Filter berita terkait (kecuali berita saat ini)
                const filteredNews = allNews.filter(item => item.id != params.id);
                setRelatedNews(filteredNews);

            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [params.id]);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('id-ID', options);
    };

    // Format time
    const formatTime = (dateString) => {
        if (!dateString) return '';
        const options = { hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleTimeString('id-ID', options);
    };

    // Share functions
    const shareOnFacebook = () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
    };

    const shareOnTwitter = () => {
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(newsDetail?.nama || '')}`, '_blank');
    };

    const shareOnLinkedIn = () => {
        window.open(`https://www.linkedin.com/sharing/share-offscreen/?url=${encodeURIComponent(window.location.href)}`, '_blank');
    };

    const shareOnWhatsApp = () => {
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(newsDetail?.nama || '')}%20${encodeURIComponent(window.location.href)}`, '_blank');
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href);
        alert('Link berhasil disalin!');
    };

    // Get reading time
    const getReadingTime = (content) => {
        if (!content) return '1 menit';
        const wordsPerMinute = 200;
        const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
        const minutes = Math.ceil(words / wordsPerMinute);
        return `${minutes} menit`;
    };

    // Social Share Button Component
    const SocialShareButton = ({ onClick, color, hoverColor, icon: Icon, label }) => (
        <button
            onClick={onClick}
            className={`p-2 ${color} hover:${hoverColor} text-white rounded-lg transition-all duration-200 hover:scale-110`}
            aria-label={label}
        >
            <Icon className="w-4 h-4" />
        </button>
    );

    if (isLoading) {
        return (
            <>
                <NavbarBottom />
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#0889c1] mx-auto"></div>
                        <p className="mt-4 text-gray-600 font-medium">Memuat berita...</p>
                    </div>
                </div>
            </>
        );
    }

    if (!newsDetail) {
        return (
            <>
                <NavbarBottom />
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center p-4">
                    <div className="text-center bg-white p-8 rounded-2xl shadow-xl max-w-md">
                        <Newspaper className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">Berita Tidak Ditemukan</h3>
                        <p className="text-gray-500 mb-6">Maaf, berita yang Anda cari tidak tersedia</p>
                        <Link to="/news" className="inline-flex items-center gap-2 px-6 py-3 bg-[#0889c1] text-white rounded-lg hover:bg-[#067aab] transition-colors">
                            <ArrowLeft className="w-4 h-4" />
                            Kembali ke Berita
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavbarBottom />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
                <div className="container max-w-7xl mx-auto">
                    {/* Back Button */}
                    <Link
                        to="/news"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 hover:bg-[#0889c1] hover:text-white group mb-6"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                        <span className="text-sm font-medium">Kembali ke Berita</span>
                    </Link>

                    <div className="flex flex-wrap -mx-4">
                        {/* Main Content */}
                        <div className="w-full lg:w-8/12 px-4 mb-8">
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                                {/* Featured Image */}
                                <div className="relative">
                                    {newsDetail.images ? (
                                        <img
                                            src={urlImg + newsDetail.images}
                                            alt={newsDetail.nama}
                                            className="w-full h-auto max-h-[500px] object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = no_image_available;
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={no_image_available}
                                            alt="No image available"
                                            className="w-full h-64 object-cover"
                                        />
                                    )}

                                    {/* Category Badge */}
                                    {newsDetail.category_name && (
                                        <div className="absolute top-4 left-4">
                                            <span className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[#0889c1] to-purple-600 rounded-full shadow-lg">
                                                {newsDetail.category_name}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-6 md:p-8">
                                    {/* Title */}
                                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight"
                                        style={{ fontFamily: 'MuseoSansRounded700' }}>
                                        {newsDetail.nama}
                                    </h1>

                                    {/* Meta Info */}
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-6">
                                        {newsDetail.tgl && (
                                            <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-full">
                                                <Calendar className="w-4 h-4 text-[#0889c1]" />
                                                <span>{formatDate(newsDetail.tgl)}</span>
                                            </div>
                                        )}
                                        {newsDetail.tgl && (
                                            <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-full">
                                                <Clock className="w-4 h-4 text-[#0889c1]" />
                                                <span>{formatTime(newsDetail.tgl)}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-full">
                                            <Eye className="w-4 h-4 text-[#0889c1]" />
                                            <span>{getReadingTime(newsDetail.isi)} membaca</span>
                                        </div>
                                    </div>

                                    {/* Share Buttons */}
                                    <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                                        <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Share2 className="w-4 h-4 text-[#0889c1]" />
                                            Bagikan:
                                        </span>
                                        <div className="flex gap-2">
                                            <SocialShareButton
                                                onClick={shareOnFacebook}
                                                color="bg-blue-600"
                                                hoverColor="bg-blue-700"
                                                icon={() => (
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                                    </svg>
                                                )}
                                                label="Share on Facebook"
                                            />
                                            <SocialShareButton
                                                onClick={shareOnTwitter}
                                                color="bg-sky-500"
                                                hoverColor="bg-sky-600"
                                                icon={() => (
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                                    </svg>
                                                )}
                                                label="Share on Twitter"
                                            />
                                            <SocialShareButton
                                                onClick={shareOnLinkedIn}
                                                color="bg-blue-700"
                                                hoverColor="bg-blue-800"
                                                icon={() => (
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                    </svg>
                                                )}
                                                label="Share on LinkedIn"
                                            />
                                            <SocialShareButton
                                                onClick={shareOnWhatsApp}
                                                color="bg-green-500"
                                                hoverColor="bg-green-600"
                                                icon={() => (
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                                    </svg>
                                                )}
                                                label="Share on WhatsApp"
                                            />
                                            <SocialShareButton
                                                onClick={copyToClipboard}
                                                color="bg-gray-600"
                                                hoverColor="bg-gray-700"
                                                icon={Link2}
                                                label="Copy link"
                                            />
                                        </div>
                                    </div>

                                    {/* Article Content */}
                                    <div className="prose prose-lg max-w-none">
                                        <div
                                            dangerouslySetInnerHTML={{ __html: newsDetail.isi }}
                                            className="text-gray-700 leading-relaxed"
                                            style={{ fontFamily: 'MuseoSansRounded400', fontSize: 16 }}
                                        />
                                    </div>

                                    {/* Tags if available */}
                                    {newsDetail.tags && (
                                        <div className="mt-8 pt-6 border-t border-gray-100">
                                            <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                                                <Tag className="w-4 h-4 text-[#0889c1]" />
                                                Tags:
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {newsDetail.tags.split(',').map((tag, index) => (
                                                    <span key={index} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-[#0889c1] hover:text-white transition-colors duration-200 cursor-default">
                                                        #{tag.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="w-full lg:w-4/12 px-4 mb-8">
                            {/* Related News */}
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-4">
                                <div className="bg-gradient-to-r from-[#0889c1] to-purple-600 px-6 py-4">
                                    <h3 className="font-bold text-white text-lg flex items-center gap-2">
                                        <Newspaper className="w-5 h-5" />
                                        BERITA LAINNYA
                                    </h3>
                                </div>

                                <div className="p-4 max-h-[600px] overflow-y-auto">
                                    {relatedNews.length > 0 ? (
                                        relatedNews.slice(0, 5).map((data_news, index) => (
                                            <Link
                                                to={`/detail-berita/${data_news.id}/${data_news.link}`}
                                                key={index}
                                                className="flex gap-4 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200 group border-b border-gray-100 last:border-0"
                                            >
                                                <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                                                    <img
                                                        src={data_news.images ? urlImg + data_news.images : no_image_available}
                                                        alt={data_news.nama}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = no_image_available;
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-semibold text-gray-800 group-hover:text-[#0889c1] transition-colors duration-200 line-clamp-2"
                                                        style={{ fontFamily: 'MuseoSansRounded700' }}>
                                                        {data_news.nama}
                                                    </h4>
                                                    {data_news.tgl && (
                                                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(data_news.tgl)}
                                                        </p>
                                                    )}
                                                    <div className="mt-1 flex items-center text-[#0889c1] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                        Baca selengkapnya
                                                        <ChevronRight className="w-3 h-3 ml-0.5 group-hover:translate-x-0.5 transition-transform" />
                                                    </div>
                                                </div>
                                            </Link>
                                        ))
                                    ) : (
                                        <div className="text-center py-8">
                                            <p className="text-gray-500 text-sm">Tidak ada berita lain</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DetailBlog;